import { EASE } from "@/components/home/animations";
import { siteConfig } from "@/data/siteData";
import { Link } from "@tanstack/react-router";
import { motion } from "framer-motion";

const Section = ({ children, alt = false, nr }: { children: React.ReactNode; alt?: boolean; nr?: string }) => (
  <section className={alt ? "bg-card" : "bg-background"}>
    <div className="max-w-[800px] mx-auto px-3 md:px-6 py-16 md:py-24 lg:py-32">
      {children}
    </div>
  </section>
);

const AGB = () => (
  <>

    {/* Minimal Hero */}
    <header className="relative bg-card pt-36 md:pt-44 pb-14 md:pb-20 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-3 md:px-6">
        <motion.p
          initial={false}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, ease: EASE, delay: 0.2 }}
          className="text-brand-label text-brand-ink mb-4"
        >
          RECHTLICHES
        </motion.p>
        <motion.h1
          initial={false}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.3 }}
          className="text-brand-heading text-foreground uppercase"
        >
          AGB
        </motion.h1>
      </div>
    </header>

    <nav className="design-legal-index" aria-label="Inhalt"><div><p>Auf dieser Seite</p><ul><li><a href="#legal-1">1. Geltungsbereich</a></li><li><a href="#legal-2">2. Angebot & Vertragsschluss</a></li><li><a href="#legal-3">3. Preise & Zahlung</a></li><li><a href="#legal-4">4. Termine & Wartezeiten</a></li><li><a href="#legal-5">5. Haftung</a></li><li><a href="#legal-6">6. Reifenhotel (Einlagerung)</a></li><li><a href="#legal-7">7. Gewährleistung</a></li><li><a href="#legal-8">8. Datenschutz</a></li><li><a href="#legal-9">9. Schlussbestimmungen</a></li></ul></div></nav>

    <Section>
      <h2 id="legal-1" className="text-brand-subheading mb-4">1. Geltungsbereich</h2>
      <p className="text-brand-body text-muted-foreground">
        Diese Allgemeinen Geschäftsbedingungen (AGB) gelten für alle Dienstleistungen und Produkte der VMAX Services AG (Marke: {siteConfig.firmaName}), Schwarzhaarhalde 4, 4665 Oftringen. Mit der Inanspruchnahme unserer Dienstleistungen akzeptierst du diese AGB.
      </p>
    </Section>

    <Section alt>
      <h2 id="legal-2" className="text-brand-subheading mb-4">2. Angebot & Vertragsschluss</h2>
      <p className="text-brand-body text-muted-foreground">
        Unsere Angebote sind freibleibend. Ein Vertrag kommt zustande, sobald du einen Auftrag erteilst und wir diesen annehmen. Bei Arbeiten ohne Voranmeldung (Walk-in) gilt die Übergabe deines Fahrzeugs als Auftragserteilung.
      </p>
    </Section>

    <Section>
      <h2 id="legal-3" className="text-brand-subheading mb-4">3. Preise & Zahlung</h2>
      <div className="text-brand-body text-muted-foreground space-y-3">
        <p>
          Alle Preise verstehen sich in Schweizer Franken (CHF) inklusive Mehrwertsteuer, sofern nicht anders angegeben. Die aktuellen Preise findest du auf unserer Website oder vor Ort.
        </p>
        <p>
          Wir akzeptieren folgende Zahlungsmittel:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Bargeld</li>
          <li>Debitkarte (EC)</li>
          <li>Kreditkarte (Visa, Mastercard)</li>
          <li>TWINT</li>
        </ul>
        <p>
          Die Zahlung ist bei Abholung des Fahrzeugs fällig.
        </p>
      </div>
    </Section>

    <Section alt>
      <h2 id="legal-4" className="text-brand-subheading mb-4">4. Termine & Wartezeiten</h2>
      <p className="text-brand-body text-muted-foreground">
        Unsere Dienstleistungen werden mehrheitlich ohne Terminvereinbarung angeboten (Walk-in). Wartezeiten können je nach Auslastung variieren. Angegebene Bearbeitungszeiten sind Richtwerte und nicht verbindlich.
      </p>
    </Section>

    <Section>
      <h2 id="legal-5" className="text-brand-subheading mb-4">5. Haftung</h2>
      <div className="text-brand-body text-muted-foreground space-y-3">
        <p>
          Wir haften für Schäden, die durch unsere Mitarbeitenden im Rahmen der Auftragsausführung verursacht werden, gemäss den gesetzlichen Bestimmungen. Die Haftung für indirekte Schäden und entgangenen Gewinn ist ausgeschlossen.
        </p>
        <p>
          Vorbestehende Schäden am Fahrzeug, die nicht durch unsere Arbeit verursacht wurden, fallen nicht in unsere Haftung. Wir empfehlen, den Zustand deines Fahrzeugs bei Übergabe zu dokumentieren.
        </p>
      </div>
    </Section>

    <Section alt>
      <h2 id="legal-6" className="text-brand-subheading mb-4">6. Reifenhotel (Einlagerung)</h2>
      <div className="text-brand-body text-muted-foreground space-y-3">
        <p>
          Die Einlagerung von Reifen/Rädern erfolgt in klimatisierten und versicherten Räumlichkeiten. Der Einlagerungsvertrag gilt jeweils für eine Saison und verlängert sich automatisch, wenn die Räder nicht abgeholt werden.
        </p>
        <p>
          Die Versicherung deckt Brand- und Wasserschäden. Natürlicher Verschleiss und Alterung sind ausgenommen. Bei Kündigung der Einlagerung sind die Räder innerhalb von 30 Tagen abzuholen.
        </p>
      </div>
    </Section>

    <Section>
      <h2 id="legal-7" className="text-brand-subheading mb-4">7. Gewährleistung</h2>
      <p className="text-brand-body text-muted-foreground">
        Für verkaufte Produkte (Reifen, Felgen, Kompletträder) gelten die gesetzlichen Gewährleistungsfristen. Reklamationen sind unverzüglich nach Feststellung des Mangels zu melden.
      </p>
    </Section>

    <Section alt>
      <h2 id="legal-8" className="text-brand-subheading mb-4">8. Datenschutz</h2>
      <p className="text-brand-body text-muted-foreground">
        Informationen zur Erhebung und Verarbeitung deiner Daten findest du in unserer <Link to="/datenschutz/" className="text-foreground hover:text-brand-ink transition-colors underline">Datenschutzerklärung</Link>.
      </p>
    </Section>

    <Section>
      <h2 id="legal-9" className="text-brand-subheading mb-4">9. Schlussbestimmungen</h2>
      <div className="text-brand-body text-muted-foreground space-y-3">
        <p>
          Es gilt Schweizer Recht. Gerichtsstand ist Oftringen (AG). Sollten einzelne Bestimmungen dieser AGB unwirksam sein, bleibt die Gültigkeit der übrigen Bestimmungen davon unberührt.
        </p>
        <p>
          Wir behalten uns vor, diese AGB jederzeit anzupassen. Es gilt jeweils die auf der Website veröffentlichte Fassung.
        </p>
      </div>
      <p className="text-brand-body text-muted-foreground mt-6 text-sm opacity-70">
        Stand: April 2026
      </p>
    </Section>
  </>
);

export default AGB;
